import { useRef, useState } from "react";
import { Check, Loader2, Trash2, UserSquare2, X } from "lucide-react";
import type { AgendaSettings } from "@bookdental/schemas";
import { Button, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@bookdental/ui";
import { PatientFormDialog } from "@/components/patients/PatientFormDialog";
import { PatientCombobox } from "./PatientCombobox";
import { CabecalhoDaConsulta } from "./consulta/CabecalhoDaConsulta";
import { CampoDaConsulta, bareInputCls } from "./consulta/CampoDaConsulta";
import { LembreteDaConsulta } from "./consulta/LembreteDaConsulta";
import { QuandoDaConsulta } from "./consulta/QuandoDaConsulta";
import { SalaDaConsulta } from "./consulta/SalaDaConsulta";
import { ServicosDaConsulta } from "./consulta/ServicosDaConsulta";
import { StatusDaConsulta } from "./consulta/StatusDaConsulta";
import { useFormularioDaConsulta } from "./consulta/use-formulario-da-consulta";
import { useEmOutraOrganizacao } from "@/components/organization/painel/OrganizacaoEmFoco";
import { AJUDA_AGENDA } from "@/lib/ajuda/dialogos/agenda";
import type { DialogInitial } from "./consulta/formulario-da-consulta";

export type { DialogInitial } from "./consulta/formulario-da-consulta";

type Props = {
  open: boolean;
  mode: "create" | "edit";
  initial: DialogInitial;
  settings: AgendaSettings;
  onClose: () => void;
  onSaved: () => void;
};

/**
 * Nova consulta / editar consulta. Ordem de quem marca: onde e com quem (cabeçalho), paciente,
 * quando, o quê — serviço é detalhe e às vezes nem é preenchido.
 */
export function AgendaAppointmentDialog(props: Props) {
  const { open, mode, settings, onClose } = props;
  const f = useFormularioDaConsulta(props);
  // Em outra clínica (sem trocar a ativa), a ficha abriria na organização errada.
  const emOutraOrganizacao = useEmOutraOrganizacao();
  const { values, update } = f;
  // Quando o paciente foi escolhido na lista — o "Ver perfil" ignora cliques logo em seguida
  // (eco do toque no iOS, ver o onClick dele).
  const pacienteEscolhidoEm = useRef(0);
  // Nome digitado no combobox sem achar cadastro — abre o cadastro completo em vez de só guardar o
  // nome solto (ver PatientCombobox onCreateNew).
  const [rascunhoDePaciente, setRascunhoDePaciente] = useState<string | null>(null);

  const cancelar = () => {
    f.voltarClinica();
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(o) => !o && !f.salvando && cancelar()}>
        <DialogContent
          className="max-w-xl"
          onOpenAutoFocus={(e) => {
            if (mode === "edit") e.preventDefault();
          }}
        >
          <DialogHeader className="flex-row items-center gap-3 space-y-0">
            <DialogTitle ajuda={mode === "create" ? AJUDA_AGENDA.novaConsulta : AJUDA_AGENDA.editarConsulta}>
              {mode === "create" ? "Nova consulta" : "Editar consulta"}
            </DialogTitle>
            <StatusDaConsulta value={values.status} onChange={(status) => update({ status })} />
          </DialogHeader>

          <div className="space-y-3">
            <CabecalhoDaConsulta
              clinica={f.clinica.nome}
              clinicaId={f.clinica.id}
              opcoesDeClinica={f.clinica.opcoes}
              trocandoDeClinica={f.clinica.trocando}
              onTrocarClinica={f.clinica.trocar}
              dentistas={f.dentistas}
              dentistaId={values.dentistId}
              onDentista={(d) => update({ dentistId: d.id, dentistName: d.name })}
            />

            {/* "Ver perfil" vive no slot de ação do rótulo: como botão ao lado, encolhia o campo de
                paciente e o formulário inteiro perdia o alinhamento por um atalho secundário. */}
            <CampoDaConsulta
              label="Paciente *"
              action={
                values.patient.id && !emOutraOrganizacao ? (
                  <button
                    type="button"
                    onClick={() => {
                      // Cinto de segurança contra o clique-fantasma do iOS: se o paciente acabou de
                      // ser escolhido, esse clique é o eco do toque na lista — não uma intenção.
                      if (Date.now() - pacienteEscolhidoEm.current < 600) return;
                      window.open(`/pacientes/${values.patient.id}`, "_blank");
                    }}
                    title="Ver perfil do paciente — adicione telefone e outras informações lá"
                    className="inline-flex shrink-0 items-center gap-1 rounded text-[11px] font-semibold text-brand-600 transition-colors hover:text-brand-700 dark:text-primary dark:hover:text-primary/80"
                  >
                    <UserSquare2 className="h-3 w-3" />
                    Ver perfil
                  </button>
                ) : undefined
              }
            >
              <PatientCombobox
                value={values.patient}
                onChange={(patient) => {
                  if (patient.id && patient.id !== values.patient.id) pacienteEscolhidoEm.current = Date.now();
                  update({ patient });
                }}
                autoFocus={mode === "create"}
                onCreateNew={setRascunhoDePaciente}
              />
            </CampoDaConsulta>

            <QuandoDaConsulta
              valor={{ date: values.date, startTime: values.startTime, endTime: values.endTime }}
              onChange={update}
              settings={settings}
              conflito={f.conflito}
              sala={<SalaDaConsulta salas={f.sala.salas} valor={values.salaId} onChange={f.sala.escolher} />}
            />

            <ServicosDaConsulta
              comCatalogo={f.comCatalogo}
              servicos={f.servicos}
              selecionados={values.serviceIds}
              atribuidos={f.atribuidos}
              texto={values.procedure}
              textoDosServicos={f.textoDosServicos}
              onAlternar={f.alternarServico}
              onTexto={(procedure) => update({ procedure })}
              onLimpar={f.limparServicos}
              onCatalogoMudou={f.recarregarServicos}
            />

            <CampoDaConsulta label="Observações">
              <textarea
                className={`${bareInputCls} resize-none`}
                value={values.notes}
                onChange={(e) => update({ notes: e.target.value })}
                rows={2}
                placeholder="Notas clínicas, observações..."
              />
            </CampoDaConsulta>

            {/* Lembrete WhatsApp some pra quem não tem a feature. */}
            {f.comLembrete && (
              <LembreteDaConsulta ligado={values.sendReminder} onChange={(sendReminder) => update({ sendReminder })} reminderTime={settings.reminderTime} />
            )}
          </div>

          {/* Lado a lado também no celular: empilhado, o par ocupava duas faixas cheias no fim de um
              diálogo que já é longo. Excluir vira só o ícone quando o espaço é curto. */}
          <DialogFooter className="-mx-6 flex-row items-center gap-2 border-t border-zinc-100 px-6 pt-3 dark:border-border sm:gap-2">
            {mode === "edit" && (
              <Button
                variant="outline"
                onClick={f.excluir}
                disabled={f.excluindo}
                aria-label="Excluir consulta"
                title="Excluir consulta"
                className="shrink-0 px-3 text-red-600 hover:bg-red-50"
              >
                {f.excluindo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                <span className="hidden sm:inline">Excluir</span>
              </Button>
            )}
            <div className="hidden flex-1 sm:block" />
            <Button variant="outline" onClick={cancelar} disabled={f.salvando} className="flex-1 whitespace-nowrap sm:flex-none">
              <X className="h-4 w-4" />
              Cancelar
            </Button>
            <button
              type="button"
              onClick={f.salvar}
              disabled={f.salvando || f.clinica.trocando || !values.patient.name.trim() || f.bloqueado}
              title={!values.patient.name.trim() ? "Informe o paciente" : undefined}
              className="inline-flex h-9 flex-[1.4] items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-brand-600 px-4 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-60 sm:flex-none"
            >
              {f.salvando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              {f.salvando ? "Salvando..." : mode === "create" ? "Criar consulta" : "Salvar"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PatientFormDialog
        open={rascunhoDePaciente !== null}
        initialName={rascunhoDePaciente ?? undefined}
        onClose={() => setRascunhoDePaciente(null)}
        onCreated={(patient) => update({ patient: { id: patient.id, name: patient.name, phone: patient.phone ?? "" } })}
      />
    </>
  );
}
